import { Formik } from "formik";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import * as Yup from "yup";

import useTestResults from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/hooks/useTestResults";
import { BoxHeadline } from "@src/modules/DeviceStatus/Devices/DeviceInfoView/shared/components";
import { HorizontalLine } from "@src/modules/shared/components/components";
import useImagesRange, {
  ImagesRangeFormValues,
} from "@src/modules/shared/components/Results/ImagesRange/hooks/useImagesRange";
import {
  ImagesRangeDescription,
  ImagesRangeWrapper,
} from "@src/modules/shared/components/Results/ImagesRange/ImagesRange.components";
import ImagesRangeForm from "@src/modules/shared/components/Results/ImagesRange/ImagesRangeForm";

interface Props {
  selectedStudyId: string;
  selectedTestId: string;
  isCustomTest?: boolean;
}

const ImagesRange: FunctionComponent<Props> = ({
  selectedStudyId,
  selectedTestId,
  isCustomTest = false,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: testResultsData } = useTestResults(
    selectedStudyId,
    selectedTestId,
  );
  const mutation = useImagesRange(selectedStudyId, selectedTestId);

  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (!!testResultsData && isCustomTest)
      setIsEdit(!!!testResultsData.imagesRange);
  }, [testResultsData, isCustomTest, setIsEdit]);

  const initialValues: ImagesRangeFormValues = testResultsData?.imagesRange
    ? { ...testResultsData?.imagesRange }
    : {};

  const validationSchema: Yup.SchemaOf<ImagesRangeFormValues> =
    Yup.object().shape({
      start: Yup.number().required().min(1),
      end: Yup.number().required().min(Yup.ref("start")),
    });

  const handleSubmit = (values: ImagesRangeFormValues) => {
    if (selectedStudyId && selectedTestId)
      mutation.mutate(values, {
        onSuccess: () => {
          toast.success(t("Changes successfully saved"));
          setIsEdit(false);
          queryClient.invalidateQueries();
        },
      });
  };

  const hasSelectedRange = !!testResultsData?.imagesRange;

  return (
    <>
      <HorizontalLine />
      <ImagesRangeWrapper>
        <BoxHeadline>{t("Images range")}</BoxHeadline>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formProps) => (
            <ImagesRangeForm
              {...formProps}
              isEdit={isEdit}
              setIsEdit={setIsEdit}
              hasSelectedRange={hasSelectedRange}
              maxValue={testResultsData?.dicomsCount ?? 0}
            />
          )}
        </Formik>
        {!isCustomTest && (
          <ImagesRangeDescription>
            {t("Changing image ranges will require study to be reanalyzed")}
          </ImagesRangeDescription>
        )}
      </ImagesRangeWrapper>
    </>
  );
};

export default ImagesRange;
